import { getWedding } from "../apis/wedding/get.wedding";
import { fetchSpouseUser } from "../hooks/store";
import {
  WebsiteStore,
  WebsiteView,
  editorState,
  getEditorStepsFromCurrentEditorState,
  setWebsiteContentStatus,
  updateEditor,
} from "../utils/editor";

import { getAllAsoEbi } from "../apis/aso-ebi/get.all.aso.ebi";
import { getAllBanks } from "../apis/aso-ebi/get.all.banks";
import { getAsoEbiSalesHistory } from "../apis/aso-ebi/get.aso.ebi.sales.history";
import { getSettlementBalance } from "../apis/aso-ebi/get.settlement.balance";
import { getSettlementHistory } from "../apis/aso-ebi/get.settlement.history";
import { getAllWeddingReminders } from "../apis/reminder/get.all.wedding.reminders";
import { getDdayInsights } from "../apis/wedding/get.dday.insights";
import { getInsights } from "../apis/wedding/get.insights";
import { updateMailTypeInWeddingReminderStore } from "../pages/Guests/Messaging/types";
import { getAllWeddingParties } from "../pages/Website/views/WeddingParty/wedding.party.utils";
import { MailType } from "../types/mailcredit";
import { TransactionCtx, TransactionStatus } from "../types/transaction";
import { UserWeddingRole } from "../types/wedding";
import { AsoEbiStore, BanksStore } from "../utils/aso.ebi";
import { ClientStorage, ClientStorageItem } from "../utils/clientstorage";

// The API layer hands us a raw `string | Error` here. The Website page shows
// this verbatim in its error state, so anything unrecognised falls back to a
// generic-but-actionable message rather than leaking `[object Object]`.
const toWebsiteContentErrorMessage = (err?: unknown): string => {
  const message =
    typeof err === "string"
      ? err
      : err instanceof Error
        ? err.message
        : typeof (err as { message?: unknown })?.message === "string"
          ? ((err as { message: string }).message)
          : "";

  const trimmed = message.trim();
  if (!trimmed) {
    return "We couldn't load your wedding details. Please try again.";
  }
  return trimmed;
};

export const getBasicWeddingInformation = (XAccountHash?: string) => {
  // Keep already-rendered content on screen during a background reload so a
  // refresh never blanks the page the user is currently looking at.
  setWebsiteContentStatus(
    WebsiteStore.contentStatus === "success" ? "refreshing" : "loading",
  );

  getWedding(
    {
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      onCompleted({ data }) {
        if (!data) {
          setWebsiteContentStatus(
            "error",
            "We couldn't find your wedding details. Please try again.",
          );
          return;
        }
        AsoEbiStore.asoEbiStatus = data.asoEbiStatus;
        AsoEbiStore.asoEbiTransactionCostBearer =
          data.asoEbiTransactionCostBearer;
        const currentEditorState = updateEditor(data);
        const { shouldDisplayPreview } =
          getEditorStepsFromCurrentEditorState(currentEditorState);
        WebsiteStore.websiteView = shouldDisplayPreview
          ? WebsiteView.Preview
          : WebsiteView.Template;
        // The editor state is hydrated at this point, so the Website page has
        // everything it needs to render. The secondary requests below are
        // fire-and-forget and must not gate the page.
        setWebsiteContentStatus("success");
        const { brideID, groomID, creatorRole } = data;
        if (creatorRole === UserWeddingRole.Groom) {
          if (brideID) {
            fetchSpouseUser(brideID);
          }
        } else {
          if (groomID) {
            fetchSpouseUser(groomID);
          }
        }

        getInsights({
          headers: {
            "X-Account-Hash": XAccountHash || "",
          },
          onCompleted(params) {
            WebsiteStore.insights = {
              pageviews: params.data.pageviews,
              rsvps: params.data.rsvps,
            };
          },
          onError(err) {
            console.error(err);
          },
        });

        getAllAsoEbi({
          headers: {
            "X-Account-Hash": XAccountHash || "",
          },
          query: {
            weddingID: data._id,
          },
          onCompleted(params) {
            AsoEbiStore.asoEbis = params.data;
          },
          onError(err) {
            console.error(err);
          },
        });

        getAllWeddingParties(data._id, XAccountHash);

        getAsoEbiSalesHistory({
          headers: {
            "X-Account-Hash": XAccountHash || "",
          },
          query: {
            transactionCtx: TransactionCtx.TxCtxBuyAsoEbi,
            transactionStatus: TransactionStatus.TxStatusSuccess,
            page: 1,
          },
          onCompleted(params) {
            AsoEbiStore.transactions = params.data.transactionList;
          },
          onError(err) {
            console.error(err);
          },
        });

        getSettlementBalance({
          headers: {
            "X-Account-Hash": XAccountHash || "",
          },
          onCompleted(params) {
            AsoEbiStore.settlementBalance = params.data.balance;
          },
          onError(err) {
            console.error(err);
          },
        });

        getSettlementHistory({
          headers: {
            "X-Account-Hash": XAccountHash || "",
          },
          onCompleted(params) {
            AsoEbiStore.settlementHistory = params.data;
          },
          onError(err) {
            console.error(err);
          },
        });
      },
      onError(err) {
        console.error("get wedding error --> ", err);
        setWebsiteContentStatus("error", toWebsiteContentErrorMessage(err));
      },
    },
    { bypassCache: true }
  );

  getAllWeddingReminders(
    {
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      onCompleted(params) {
        for (const reminder of params.data) {
          if (reminder.mailType === MailType.Countdown) {
            updateMailTypeInWeddingReminderStore(MailType.Countdown, reminder);
          }
        }
      },
      onError(err) {},
    },
    { bypassCache: true }
  );

  getAllBanks({
    headers: {
      "X-Account-Hash": XAccountHash || "",
    },
    onCompleted(params) {
      BanksStore.data = params.data;
    },
    onError(err) {
      console.error(err);
    },
  });

  getDdayInsights(
    {
      headers: {
        "X-Account-Hash": XAccountHash || "",
      },
      onCompleted(params) {
        WebsiteStore.ddayInsights = params.data;
      },
      onError() {},
    },
    { bypassCache: true }
  );
};

// Retry entry point for the Website page's error state. The editor state is
// empty when the first request failed, so fall back to the persisted account
// hash to make sure the retry is scoped to the right wedding.
export const reloadBasicWeddingInformation = () => {
  const XAccountHash =
    editorState.XAccountHash ||
    ClientStorage.fetch(ClientStorageItem.XAccountHash) ||
    undefined;
  return getBasicWeddingInformation(XAccountHash);
};
